import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { createProduct, updateProduct, getProduct, getCategories, translateProduct } from '@/lib/api';
import { Loader2, Plus, X, Upload, Image, FileText, CheckCircle2, Store, Languages, AlertCircle, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import ProPaywall from '@/components/ProPaywall';
import AiPhotoFill from '@/components/AiPhotoFill';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductForm = ({ isNew = false }) => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { isUSA } = useLocale();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});
  const [colorInput, setColorInput] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [addToStore, setAddToStore] = useState(false);
  const [hasStore, setHasStore] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState('default');
  const [accountStatus, setAccountStatus] = useState(null);
  const [optimizingImage, setOptimizingImage] = useState(null); // Index of image being optimized
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    category: '',
    subcategory: '',
    description: '',
    description_en: '',
    composition: '',
    weight_gsm: '',
    width_cm: '',
    available_colors: [],
    moq: '',
    unit: 'Mt',  // Mt, Yd, Kg for fabrics; pzas/Units for clothing
    delivery_time: '',
    origin: 'nacional',
    reference_price: '',
    currency: isUSA ? 'USD' : 'MXN',
    hide_price: false,
    images: [],
    tech_sheet_url: '',
    tech_sheet_name: '',
    availability: 'disponible',
    sample_available: true,
    publish_region: 'MX'  // MX, US, or BOTH
  });

  // Unit options based on category
  const getUnitOptions = () => {
    if (formData.category === 'telas') {
      return [
        { value: 'Mt', label: isUSA ? 'Meters (Mt)' : 'Metros (Mt)' },
        { value: 'Yd', label: isUSA ? 'Yards (Yd)' : 'Yardas (Yd)' },
        { value: 'Kg', label: isUSA ? 'Kilograms (Kg)' : 'Kilogramos (Kg)' }
      ];
    } else {
      return [
        { value: 'pzas', label: isUSA ? 'Units' : 'Piezas (pzas)' },
        { value: 'Units', label: 'Units' }
      ];
    }
  };

  const showEnglishFields = formData.publish_region === 'US' || formData.publish_region === 'BOTH';

  const handleTranslate = async () => {
    if (!formData.name && !formData.description) {
      toast.error(isUSA ? 'Add name and description first' : 'Agrega nombre y descripción primero');
      return;
    }
    setTranslating(true);
    try {
      const result = await translateProduct(formData.name, formData.description);
      setFormData(prev => ({
        ...prev,
        name_en: result.name_en || prev.name_en,
        description_en: result.description_en || prev.description_en
      }));
      toast.success(isUSA ? 'Translation completed!' : '¡Traducción completada!');
    } catch (error) {
      toast.error(isUSA ? 'Translation failed' : 'Error al traducir');
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cats = await getCategories(isUSA ? 'en' : 'es');
        setCategories(cats);

        const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
        if (token) {
          // Fetch account status (product limits)
          try {
            const statusRes = await fetch(`${API_URL}/api/subscriptions/account-status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              setAccountStatus(statusData);
            }
          } catch (e) {
            console.log('Error fetching account status');
          }

          // Check if user has a store
          try {
            const storeRes = await fetch(`${API_URL}/api/stores/my`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (storeRes.ok) {
              const storeData = await storeRes.json();
              setHasStore(!!storeData);
            }
          } catch (e) {
            console.log('No store found');
          }
        }

        if (!isNew && productId) {
          const product = await getProduct(productId);
          setFormData({
            name: product.name || '',
            name_en: product.name_en || '',
            category: product.category || '',
            subcategory: product.subcategory || '',
            description: product.description || '',
            description_en: product.description_en || '',
            composition: product.composition || '',
            weight_gsm: product.weight_gsm?.toString() || '',
            width_cm: product.width_cm?.toString() || '',
            available_colors: product.available_colors || [],
            moq: product.moq?.toString() || '',
            unit: product.unit || (product.category === 'telas' ? 'Mt' : 'pzas'),
            delivery_time: product.delivery_time || '',
            origin: product.origin || 'nacional',
            reference_price: product.reference_price?.toString() || '',
            currency: product.currency || (isUSA ? 'USD' : 'MXN'),
            hide_price: product.hide_price || false,
            images: product.images || [],
            tech_sheet_url: product.tech_sheet_url || '',
            tech_sheet_name: product.tech_sheet_name || '',
            availability: product.availability || 'disponible',
            sample_available: product.sample_available !== false
          });
        }
      } catch (error) {
        toast.error(isUSA ? 'Error loading data' : 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isNew, productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const submitData = {
      ...formData,
      weight_gsm: formData.weight_gsm ? parseInt(formData.weight_gsm) : null,
      width_cm: formData.width_cm ? parseInt(formData.width_cm) : null,
      moq: formData.moq ? parseInt(formData.moq) : null,
      reference_price: formData.reference_price ? parseFloat(formData.reference_price) : null
    };

    try {
      if (isNew) {
        await createProduct(submitData);
        toast.success(isUSA ? 'Product created successfully' : 'Producto creado correctamente');

        // Also add to store if checkbox is checked
        if (addToStore && hasStore) {
          const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
          
          // Convert available_colors to variants format for store
          const storeVariants = {};
          if (formData.available_colors && formData.available_colors.length > 0) {
            storeVariants.color = formData.available_colors;
          }
          
          const storeProductData = {
            name: formData.name,
            category: formData.category,
            subcategory: formData.subcategory || '',
            description: formData.description,
            price: formData.reference_price ? parseFloat(formData.reference_price) : null,
            hide_price: formData.hide_price,
            images: formData.images,
            moq: formData.moq ? parseInt(formData.moq) : null,
            stock: null,
            tags: formData.composition ? [formData.composition] : [],
            variants: Object.keys(storeVariants).length > 0 ? storeVariants : null,
            unit: formData.unit || 'Mt'
          };

          try {
            const storeRes = await fetch(`${API_URL}/api/stores/my/products`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(storeProductData)
            });

            if (storeRes.ok) {
              toast.success(isUSA ? 'Also added to your online store' : 'También agregado a tu tienda en línea');
            } else if (storeRes.status === 403) {
              // Pro plan required for store products - just skip silently as main product was created
              toast.info(isUSA ? 'Product created. Upgrade to Pro to add it to your store.' : 'Producto creado. Activa Pro para agregarlo a tu tienda.');
            } else {
              const err = await storeRes.json();
              toast.error(isUSA 
                ? `Error adding to store: ${err.detail || 'Unknown error'}`
                : `Error al agregar a tienda: ${err.detail || 'Error desconocido'}`);
            }
          } catch (storeErr) {
            toast.error(isUSA ? 'Error adding to store' : 'Error al agregar a la tienda');
          }
        }
      } else {
        await updateProduct(productId, submitData);
        toast.success(isUSA ? 'Product updated successfully' : 'Producto actualizado correctamente');
      }
      navigate('/dashboard/products');
    } catch (error) {
      // Check for 403 - product limit or Pro plan required
      if (error.status === 403) {
        setPaywallReason('product_limit');
        setShowPaywall(true);
      } else {
        toast.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const addColor = () => {
    if (colorInput.trim() && !formData.available_colors.includes(colorInput.trim())) {
      setFormData({
        ...formData,
        available_colors: [...formData.available_colors, colorInput.trim()]
      });
      setColorInput('');
    }
  };

  const removeColor = (index) => {
    setFormData({
      ...formData,
      available_colors: formData.available_colors.filter((_, i) => i !== index)
    });
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  // Optimize image using AI Photo Optimizer
  const optimizeImage = async (index) => {
    const imageUrl = formData.images[index];
    if (!imageUrl) return;

    setOptimizingImage(index);
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');

    try {
      // Use the simple URL-based endpoint
      // Make sure the URL is absolute
      let fullImageUrl = imageUrl;
      if (imageUrl.startsWith('/')) {
        fullImageUrl = `${API_URL}${imageUrl}`;
      } else if (!imageUrl.startsWith('http')) {
        fullImageUrl = `${API_URL}/${imageUrl}`;
      }
      
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/texvision/photo-optimizer/enhance-url`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Error optimizing image'));
            }
          } catch (e) {
            reject(new Error('Error processing response'));
          }
        };
        
        xhr.onerror = () => reject(new Error('Connection error'));
        xhr.timeout = 120000; // 2 minutes timeout
        xhr.ontimeout = () => reject(new Error('Timeout - try again'));
        
        xhr.send(JSON.stringify({
          image_url: fullImageUrl,
          enhancements: ["lighting", "sharpness", "color_correction"]
        }));
      });

      if (result.optimized_url) {
        // Replace the image with the optimized version
        const newImageUrl = result.optimized_url.startsWith('http') 
          ? result.optimized_url 
          : `${API_URL}${result.optimized_url}`;
        const newImages = [...formData.images];
        newImages[index] = newImageUrl;
        setFormData(prev => ({
          ...prev,
          images: newImages
        }));
        toast.success(isUSA ? 'Image optimized successfully!' : '¡Imagen optimizada exitosamente!');
      }
    } catch (error) {
      console.error('Optimize error:', error);
      toast.error(error.message || (isUSA ? 'Error optimizing image' : 'Error al optimizar imagen'));
    } finally {
      setOptimizingImage(null);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');

    try {
      const formDataUpload = new FormData();
      for (let i = 0; i < files.length; i++) {
        formDataUpload.append('files', files[i]);
      }

      const response = await fetch(`${API_URL}/api/upload/images/multiple`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: formDataUpload
      });

      // Read response as text first to avoid body stream issues
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Error al subir imágenes';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Use default message
        }
        throw new Error(errorMessage);
      }

      const result = JSON.parse(responseText);
      
      if (result.uploaded && result.uploaded.length > 0) {
        const newImages = result.uploaded.map(img => `${API_URL}${img.url}`);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImages]
        }));
        
        // Show compression stats
        if (result.total_space_saved_mb > 0) {
          toast.success(isUSA 
            ? `${result.uploaded.length} image(s) uploaded. You saved ${result.total_space_saved_mb}MB with automatic compression.`
            : `${result.uploaded.length} imagen(es) subida(s). Ahorraste ${result.total_space_saved_mb}MB con la compresión automática.`
          );
        } else {
          toast.success(isUSA 
            ? `${result.uploaded.length} image(s) uploaded successfully`
            : `${result.uploaded.length} imagen(es) subida(s) correctamente`);
        }
      }

      if (result.errors && result.errors.length > 0) {
        result.errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error uploading images' : 'Error al subir las imágenes'));
    } finally {
      setUploadingImages(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleDocUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/upload/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: formDataUpload
      });

      // Read response as text first to avoid body stream issues
      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = 'Error al subir documento';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Use default message
        }
        throw new Error(errorMessage);
      }

      const result = JSON.parse(responseText);
      
      setFormData(prev => ({
        ...prev,
        tech_sheet_url: `${API_URL}${result.url}`,
        tech_sheet_name: result.original_name
      }));
      
      toast.success(isUSA ? 'Technical sheet uploaded successfully' : 'Ficha técnica subida correctamente');
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error uploading technical sheet' : 'Error al subir la ficha técnica'));
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) {
        docInputRef.current.value = '';
      }
    }
  };

  const removeDocument = () => {
    setFormData(prev => ({
      ...prev,
      tech_sheet_url: '',
      tech_sheet_name: ''
    }));
  };

  const subcategories = formData.category && categories[formData.category]
    ? categories[formData.category].subcategories
    : [];
  
  // Get subcategory labels for display
  const getSubcategoryLabel = (subKey) => {
    if (formData.category && categories[formData.category]?.subcategory_labels) {
      return categories[formData.category].subcategory_labels[subKey] || subKey;
    }
    return subKey;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="product-form">
      <div className="page-header">
        <h1 className="page-title">
          {isNew 
            ? (isUSA ? 'New product' : 'Nuevo producto') 
            : (isUSA ? 'Edit product' : 'Editar producto')}
        </h1>
        <p className="page-subtitle">
          {isNew 
            ? (isUSA ? 'Add a product to your catalog' : 'Agrega un producto a tu catálogo') 
            : (isUSA ? 'Update product information' : 'Actualiza la información del producto')}
        </p>
      </div>

      {/* Product limit indicator for free users */}
      {isNew && accountStatus && !accountStatus.is_pro && (
        <Alert className={`mb-4 ${accountStatus.limits.products.can_add ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
          <AlertCircle className={`h-4 w-4 ${accountStatus.limits.products.can_add ? 'text-blue-600' : 'text-amber-600'}`} />
          <AlertDescription className={accountStatus.limits.products.can_add ? 'text-blue-700' : 'text-amber-700'}>
            {accountStatus.limits.products.can_add ? (
              <>
                {isUSA 
                  ? `Products: ${accountStatus.limits.products.used}/${accountStatus.limits.products.limit} used`
                  : `Productos: ${accountStatus.limits.products.used}/${accountStatus.limits.products.limit} usados`}
                {accountStatus.limits.products.used >= accountStatus.limits.products.limit - 1 && (
                  <span className="ml-2 font-medium">
                    {isUSA ? '(Last free product!)' : '(¡Último producto gratis!)'}
                  </span>
                )}
              </>
            ) : (
              <>
                {isUSA 
                  ? `You've reached your limit of ${accountStatus.limits.products.limit} free products. `
                  : `Llegaste al límite de ${accountStatus.limits.products.limit} productos gratis. `}
                <button 
                  type="button"
                  onClick={() => { setPaywallReason('product_limit'); setShowPaywall(true); }}
                  className="font-bold underline"
                >
                  {isUSA ? 'Upgrade to PRO' : 'Actualiza a PRO'}
                </button>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {isNew && (
        <AiPhotoFill
          isUSA={isUSA}
          onFilled={(sheet, imageUrl) => setFormData(prev => ({
            ...prev,
            name: sheet.name || prev.name,
            category: sheet.category || prev.category,
            subcategory: sheet.subcategory || prev.subcategory,
            description: sheet.description || prev.description,
            composition: sheet.composition || prev.composition,
            weight_gsm: sheet.weight_gsm ? String(sheet.weight_gsm) : prev.weight_gsm,
            available_colors: sheet.colors?.length ? Array.from(new Set([...prev.available_colors, ...sheet.colors])) : prev.available_colors,
            images: imageUrl ? [...prev.images, imageUrl] : prev.images
          }))}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Basic information' : 'Información básica'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{isUSA ? 'Product name (Spanish) *' : 'Nombre del producto (Español) *'}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={isUSA ? 'Tela de algodón premium' : 'Tela de algodón premium'}
                required
                data-testid="product-name-input"
              />
            </div>

            {/* Publish Region Selector */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
              <Label className="font-medium">{isUSA ? 'Where do you want to sell?' : '¿Dónde quieres vender?'}</Label>
              <div className="flex flex-wrap gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${formData.publish_region === 'MX' ? 'bg-emerald-100 border-emerald-500 text-emerald-800' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="publish_region"
                    value="MX"
                    checked={formData.publish_region === 'MX'}
                    onChange={(e) => setFormData({ ...formData, publish_region: e.target.value })}
                    className="sr-only"
                  />
                  <span className="text-lg">🇲🇽</span>
                  <span className="font-medium">{isUSA ? 'Mexico only' : 'Solo México'}</span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${formData.publish_region === 'US' ? 'bg-blue-100 border-blue-500 text-blue-800' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="publish_region"
                    value="US"
                    checked={formData.publish_region === 'US'}
                    onChange={(e) => setFormData({ ...formData, publish_region: e.target.value })}
                    className="sr-only"
                  />
                  <span className="text-lg">🇺🇸</span>
                  <span className="font-medium">{isUSA ? 'USA only' : 'Solo USA'}</span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${formData.publish_region === 'BOTH' ? 'bg-purple-100 border-purple-500 text-purple-800' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="publish_region"
                    value="BOTH"
                    checked={formData.publish_region === 'BOTH'}
                    onChange={(e) => setFormData({ ...formData, publish_region: e.target.value })}
                    className="sr-only"
                  />
                  <span className="text-lg">🌎</span>
                  <span className="font-medium">{isUSA ? 'Both markets' : 'Ambos mercados'}</span>
                </label>
              </div>
            </div>

            {/* English Translation Section - Only shows when selling to US or BOTH */}
            {showEnglishFields && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Languages className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-900">{isUSA ? 'English Translation' : 'Traducción al Inglés'}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleTranslate}
                    disabled={translating || (!formData.name && !formData.description)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {translating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isUSA ? 'Translating...' : 'Traduciendo...'}</>
                    ) : (
                      <><Languages className="w-4 h-4 mr-2" /> {isUSA ? 'Auto-translate' : 'Traducir automático'}</>
                    )}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name_en" className="text-sm text-blue-800">{isUSA ? 'Name in English' : 'Nombre en inglés'}</Label>
                  <Input
                    id="name_en"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    placeholder="Premium cotton fabric"
                    className="bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description_en" className="text-sm text-blue-800">{isUSA ? 'Description in English' : 'Descripción en inglés'}</Label>
                  <Textarea
                    id="description_en"
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder="Product description in English..."
                    rows={3}
                    className="bg-white"
                  />
                </div>
                <p className="text-xs text-blue-600">
                  {isUSA ? '* Required for USA market visibility' : '* Requerido para visibilidad en mercado USA'}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">{isUSA ? 'Category *' : 'Categoría *'}</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value, subcategory: '' })}
                  required
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={isUSA ? 'Select category' : 'Seleccionar categoría'} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subcategory">{isUSA ? 'Subcategory *' : 'Subcategoría *'}</Label>
                <Select
                  value={formData.subcategory}
                  onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                  disabled={!formData.category}
                  required
                >
                  <SelectTrigger data-testid="subcategory-select">
                    <SelectValue placeholder={isUSA ? 'Select subcategory' : 'Seleccionar subcategoría'} />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map((sub) => (
                      <SelectItem key={sub} value={sub}>{getSubcategoryLabel(sub)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{isUSA ? 'Description *' : 'Descripción *'}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={isUSA ? 'Describe product features...' : 'Describe las características del producto...'}
                rows={4}
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Technical specifications' : 'Especificaciones técnicas'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Campos específicos para Telas e Hilos */}
            {(formData.category === 'telas' || formData.category === 'hilos_fibras') && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="composition">{isUSA ? 'Composition' : 'Composición'}</Label>
                  <Input
                    id="composition"
                    value={formData.composition}
                    onChange={(e) => setFormData({ ...formData, composition: e.target.value })}
                    placeholder={isUSA ? '100% cotton' : '100% algodón'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight_gsm">{isUSA ? 'Weight (g/m²)' : 'Gramaje (g/m²)'}</Label>
                  <Input
                    id="weight_gsm"
                    type="number"
                    value={formData.weight_gsm}
                    onChange={(e) => setFormData({ ...formData, weight_gsm: e.target.value })}
                    placeholder="180"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="width_cm">{isUSA ? 'Width (cm)' : 'Ancho (cm)'}</Label>
                  <Input
                    id="width_cm"
                    type="number"
                    value={formData.width_cm}
                    onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })}
                    placeholder="150"
                  />
                </div>
              </div>
            )}

            {/* Campos para Avíos y otros */}
            {formData.category && formData.category !== 'telas' && formData.category !== 'hilos_fibras' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="material">{isUSA ? 'Material' : 'Material'}</Label>
                  <Input
                    id="material"
                    value={formData.material || ''}
                    onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                    placeholder={isUSA ? 'e.g. Metal, Plastic, Polyester' : 'Ej. Metal, Plástico, Poliéster'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="size">{isUSA ? 'Size/Dimensions' : 'Tamaño/Dimensiones'}</Label>
                  <Input
                    id="size"
                    value={formData.size || ''}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    placeholder={isUSA ? 'e.g. 15mm, 2cm x 3cm' : 'Ej. 15mm, 2cm x 3cm'}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{isUSA ? 'Available colors' : 'Colores disponibles'}</Label>
              <div className="flex gap-2">
                <Input
                  value={colorInput}
                  onChange={(e) => setColorInput(e.target.value)}
                  placeholder={isUSA ? 'White, Black, Blue...' : 'Blanco, Negro, Azul...'}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addColor())}
                />
                <Button type="button" variant="outline" onClick={addColor}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.available_colors.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.available_colors.map((color, index) => (
                    <span key={index} className="badge bg-slate-100 text-slate-700 border-slate-200 flex items-center gap-1">
                      {color}
                      <button type="button" onClick={() => removeColor(index)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="origin">{isUSA ? 'Origin' : 'Origen'}</Label>
              <Select
                value={formData.origin}
                onValueChange={(value) => setFormData({ ...formData, origin: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nacional">{isUSA ? 'Domestic' : 'Nacional'}</SelectItem>
                  <SelectItem value="importado">{isUSA ? 'Imported' : 'Importado'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle>{isUSA ? 'Sales information' : 'Comercialización'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="moq">{isUSA ? 'MOQ (Minimum order)' : 'MOQ (Cantidad mínima)'}</Label>
                <Input
                  id="moq"
                  type="number"
                  value={formData.moq}
                  onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
                  placeholder="100"
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Unit' : 'Unidad'}</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getUnitOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery_time">{isUSA ? 'Delivery time' : 'Tiempo de entrega'}</Label>
                <Input
                  id="delivery_time"
                  value={formData.delivery_time}
                  onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                  placeholder={isUSA ? '10-15 days' : '10-15 días'}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reference_price">{isUSA ? 'Reference price' : 'Precio referencial'}</Label>
                <Input
                  id="reference_price"
                  type="number"
                  step="0.01"
                  value={formData.reference_price}
                  onChange={(e) => setFormData({ ...formData, reference_price: e.target.value })}
                  placeholder="150.00"
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Currency' : 'Moneda'}</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">🇲🇽 MXN</SelectItem>
                    <SelectItem value="USD">🇺🇸 USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="availability">{isUSA ? 'Availability' : 'Disponibilidad'}</Label>
                <Select
                  value={formData.availability}
                  onValueChange={(value) => setFormData({ ...formData, availability: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponible">{isUSA ? 'Available' : 'Disponible'}</SelectItem>
                    <SelectItem value="agotado">{isUSA ? 'Out of stock' : 'Agotado'}</SelectItem>
                    <SelectItem value="por_pedido">{isUSA ? 'Made to order' : 'Por pedido'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="hide_price">{isUSA ? 'Hide price' : 'Ocultar precio'}</Label>
                <p className="text-sm text-slate-500">{isUSA ? 'Price will only be shown upon quote request' : 'El precio solo se mostrará bajo cotización'}</p>
              </div>
              <Switch
                id="hide_price"
                checked={formData.hide_price}
                onCheckedChange={(checked) => setFormData({ ...formData, hide_price: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="sample_available">{isUSA ? 'Physical sample available' : 'Muestra física disponible'}</Label>
                <p className="text-sm text-slate-500">{isUSA ? 'Users can request samples' : 'Los usuarios podrán solicitar muestras'}</p>
              </div>
              <Switch
                id="sample_available"
                checked={formData.sample_available}
                onCheckedChange={(checked) => setFormData({ ...formData, sample_available: checked })}
              />
            </div>

            {/* Add to Store option - only show for new products and if user has a store */}
            {isNew && hasStore && (
              <div className="flex items-center justify-between py-3 px-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-emerald-600" />
                  <div>
                    <Label htmlFor="add_to_store" className="text-emerald-800">
                      {isUSA ? 'Also add to my store' : 'También agregar a mi tienda'}
                    </Label>
                    <p className="text-sm text-emerald-600">
                      {isUSA ? 'Product will appear in your online store' : 'El producto aparecerá en tu tienda en línea'}
                    </p>
                  </div>
                </div>
                <Switch
                  id="add_to_store"
                  checked={addToStore}
                  onCheckedChange={setAddToStore}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" style={{ color: '#1E4C8E' }} />
              {isUSA ? 'Product photos' : 'Fotos del producto'}
            </CardTitle>
            <CardDescription>
              {isUSA 
                ? 'Upload photos from your device. Formats: JPG, PNG, WebP (max 5MB each)'
                : 'Sube fotos de tu producto desde tu equipo. Formatos: JPG, PNG, WebP (máx. 5MB cada una)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors cursor-pointer"
              onClick={() => imageInputRef.current?.click()}
            >
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                data-testid="image-upload-input"
              />
              {uploadingImages ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  <p className="text-sm text-slate-500">{isUSA ? 'Uploading images...' : 'Subiendo imágenes...'}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-slate-400" />
                  <p className="text-sm text-slate-600 font-medium">
                    {isUSA ? 'Click to select images' : 'Haz clic para seleccionar imágenes'}
                  </p>
                  <p className="text-xs text-slate-400">
                    {isUSA ? 'or drag and drop here' : 'o arrastra y suelta aquí'}
                  </p>
                </div>
              )}
            </div>

            {/* Image Preview Grid */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative">
                    {/* Image container */}
                    <div className="relative group">
                      {optimizingImage === index ? (
                        <div className="w-full h-24 flex items-center justify-center bg-slate-100 rounded-lg border border-slate-200">
                          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                          <span className="ml-2 text-xs text-slate-500">{isUSA ? 'Optimizing...' : 'Optimizando...'}</span>
                        </div>
                      ) : (
                        <img 
                          src={img} 
                          alt={`${isUSA ? 'Image' : 'Imagen'} ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-slate-200"
                        />
                      )}
                      {/* Delete button */}
                      <button 
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={optimizingImage === index}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-1 left-1 bg-slate-900 text-white text-xs px-2 py-0.5 rounded">
                          {isUSA ? 'Main' : 'Principal'}
                        </span>
                      )}
                    </div>
                    {/* Optimize button - visible below image */}
                    <button 
                      type="button"
                      onClick={() => optimizeImage(index)}
                      disabled={optimizingImage !== null}
                      className="w-full mt-2 py-1.5 px-2 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                    >
                      <Wand2 className="w-3 h-3" />
                      {isUSA ? 'Enhance' : 'Mejorar'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {formData.images.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-2">
                {isUSA ? 'No images. The first image will be the main product image.' : 'No hay imágenes. La primera imagen será la principal del producto.'}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="card-industrial mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: '#1E4C8E' }} />
              {isUSA ? 'Technical sheet' : 'Ficha técnica'}
            </CardTitle>
            <CardDescription>
              {isUSA 
                ? 'Upload the product technical sheet. Formats: PDF, Word (max 10MB)'
                : 'Sube la ficha técnica del producto. Formatos: PDF, Word (máx. 10MB)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.tech_sheet_url ? (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1E4C8E' }}>
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">
                      {formData.tech_sheet_name || (isUSA ? 'Technical sheet' : 'Ficha técnica')}
                    </p>
                    <p className="text-xs text-slate-500">{isUSA ? 'Attached document' : 'Documento adjunto'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href={formData.tech_sheet_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm font-medium hover:underline"
                    style={{ color: '#1E4C8E' }}
                  >
                    {isUSA ? 'View' : 'Ver'}
                  </a>
                  <button 
                    type="button"
                    onClick={removeDocument}
                    className="text-red-500 hover:text-red-600 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors cursor-pointer"
                onClick={() => docInputRef.current?.click()}
              >
                <input
                  ref={docInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleDocUpload}
                  className="hidden"
                  data-testid="doc-upload-input"
                />
                {uploadingDoc ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    <p className="text-sm text-slate-500">{isUSA ? 'Uploading document...' : 'Subiendo documento...'}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-slate-400" />
                    <p className="text-sm text-slate-600 font-medium">
                      {isUSA ? 'Click to select file' : 'Haz clic para seleccionar archivo'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {isUSA ? 'PDF or Word (max 10MB)' : 'PDF o Word (máx. 10MB)'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            {isUSA ? 'Cancel' : 'Cancelar'}
          </Button>
          <Button 
            type="submit" 
            className="hover:opacity-90"
            style={{ backgroundColor: '#34B772' }}
            disabled={saving}
            data-testid="save-product-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUSA ? 'Saving...' : 'Guardando...'}
              </>
            ) : (
              isNew 
                ? (isUSA ? 'Create product' : 'Crear producto') 
                : (isUSA ? 'Save changes' : 'Guardar cambios')
            )}
          </Button>
        </div>
      </form>

      {/* Pro Paywall Modal */}
      <ProPaywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        feature={isUSA ? 'publish products' : 'publicar productos'}
        reason={paywallReason}
      />
    </div>
  );
};

export default ProductForm;
